import React from "react";
import { formatRangeVal, lucky7Markets } from "./lucky7Utils";
import { formatBook } from "../../../utils/formatOdds";

/**
 * Lucky 7 bet table (lucky7 / plucky7 / lucky7eu / lucky7eu2) in the reference admin's markup —
 * rendered inside the page's `.casino-detail`:
 *   .casino-box.low-high-box (Low | 7 card | High + range)
 *   .casino-box.sidebets-box (.lucky7-extra-bets: Even, Odd, Black, Red)
 *   .casino-box.cards-top (4 lines of 3 cards)
 *   .casino-box.cards-box (13 single cards)
 * Styled by the ported reference rules in styles/index.css.
 *
 * Markets are read like the reference (`mainData` = feed sub in API order):
 *   0 Low, 1 High, 2 Even, 3 Odd, 4 Red, 5 Black, 6-18 Card A..K, 19-22 Line 1..4 —
 * with a lookup by `nat` as fallback.
 */
const IMG = `${import.meta.env.BASE_URL}img/cards`;
const SUIT = `${import.meta.env.BASE_URL}assets/img/icons`;
const CARD_VALUES = ["A", "2", "3", "4", "5", "6", "7", "8", "9", "10", "J", "Q", "K"];
const LINES = [
  ["A", "2", "3"],
  ["4", "5", "6"],
  ["8", "9", "10"],
  ["J", "Q", "K"],
];

const isSuspended = (b) => String(b?.gstatus || "").toUpperCase() !== "OPEN";
const odds = (b) => (b?.b !== undefined && b?.b !== null && b?.b !== "" ? b.b : 0);

export default function BetTableLucky7({ sub = [], onBetClick, exposures = {} }) {
  const m = lucky7Markets(sub);
  const getExposure = (name) => {
    const v = exposures[name] ?? exposures[name?.toLowerCase?.()];
    return v != null ? Number(v) || 0 : 0;
  };
  // Reference v-casinofancybook: amount > 0 → book-green, otherwise book-red
  const book = (b, extra = "") => {
    const exp = getExposure(b?.nat);
    return <div className={`${extra}casino-book ${exp > 0 ? "book-green" : "book-red"}`}>{formatBook(exp)}</div>;
  };
  const click = (b) => {
    if (isSuspended(b)) return;
    onBetClick?.(odds(b), b?.nat, null, b);
  };
  const range = (b, align) => (
    <div className={`casino-min-max text-${align}`}>
      {"\n                                R:"}
      <span>{formatRangeVal(b?.min)}</span>-<span>{formatRangeVal(b?.max)}</span>
    </div>
  );
  const susp = (b) => (isSuspended(b) ? " suspended" : "");
  // Reference book directive adds .lock-top to a suspended parent of the book element
  const suspLock = (b) => (isSuspended(b) ? " suspended lock-top" : "");

  const lowHigh = (cls, b) => (
    <div className={`${cls}${suspLock(b)}`} onClick={() => click(b)}>
      <div className="casino-odds">{odds(b)}</div>{" "}
      <div className="text-center casino-buttons">
        <span>{b?.nat}</span>
      </div>{" "}
      {book(b)}
    </div>
  );

  const extraBet = (b, content) => (
    <div className="lucky7-extra-bets-item-container">
      <div className={`lucky7-extra-bets-item${suspLock(b)}`} onClick={() => click(b)}>
        <div className="casino-odds">{odds(b)}</div> <div className="casino-buttons">{content}</div> {book(b)}
      </div>{" "}
      {range(b, "right")}
    </div>
  );

  const cardItem = (idx) => {
    const b = m[6 + idx];
    return (
      <div className="casino-card-item">
        <div className={`card-image${susp(b)}`} onClick={() => click(b)}>
          <img src={`${IMG}/${CARD_VALUES[idx]}.png`} alt="" />
        </div>{" "}
        {book(b)}
      </div>
    );
  };

  return (
    <>
      <div className="casino-box low-high-box">
        {lowHigh("low-odds", m[0])}{" "}
        <div className="text-center lucky7-card">
          <img src={`${IMG}/7.png`} className="img-fluid" alt="" />
        </div>{" "}
        {lowHigh("high-odds", m[1])}{" "}
        {range(m[0], "right")}
      </div>{" "}
      <div className="casino-box sidebets-box">
        <div className="lucky7-extra-bets">
          {extraBet(m[2], m[2]?.nat || "Even")}{" "}
          {extraBet(m[3], m[3]?.nat || "Odd")}{" "}
          {extraBet(
            m[5],
            <>
              <img src={`${SUIT}/spade.png`} alt="" /> <img src={`${SUIT}/club.png`} alt="" />
            </>
          )}{" "}
          {extraBet(
            m[4],
            <>
              <img src={`${SUIT}/heart.png`} alt="" /> <img src={`${SUIT}/diamond.png`} alt="" />
            </>
          )}
        </div>
      </div>{" "}
      <div className="casino-box cards-top">
        <div className="container-fluid container-fluid-5">
          <div className="row row5">
            {LINES.map((cards, li) => {
              const b = m[19 + li];
              return (
                <React.Fragment key={li}>
                  {li > 0 && " "}
                  <div className="col-6 col-md-3">
                    <div>
                      <div className="casino-odds">{odds(b)}</div>{" "}
                      <div className="casino-cards text-center mt-1">
                        <div className={`casino-box cards-top-box${susp(b)}`} onClick={() => click(b)}>
                          {cards.map((c, ci) => (
                            <React.Fragment key={c}>
                              {ci > 0 && " "}
                              <div className="casino-card-item">
                                <div className="card-image">
                                  <img src={`${IMG}/${c}.png`} alt="" />
                                </div>
                              </div>
                            </React.Fragment>
                          ))}
                        </div>{" "}
                        {book(b, "w-100 ")}
                      </div>{" "}
                      {range(b, "center")}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>{" "}
      <div className="casino-box cards-box">
        <div className="w-100">
          <div className="casino-odds">
            {`${odds(m[6])} \n                                    \n                                `}
          </div>{" "}
          <div className="casino-cards text-center mt-1">
            {/* Reference: two v-for loops (A..7, 8..K) with no whitespace inside a loop, " " between them */}
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <React.Fragment key={i}>{cardItem(i)}</React.Fragment>
            ))}{" "}
            {[7, 8, 9, 10, 11, 12].map((i) => (
              <React.Fragment key={i}>{cardItem(i)}</React.Fragment>
            ))}
          </div>
        </div>{" "}
        {range(m[6], "right")}
      </div>
    </>
  );
}
